import { MessageSquare } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Chip } from "@/components/chip";
import {
  DataTable,
  NARROW_ONLY,
  NARROW_WRAP,
  type NarrowColumn,
  SortableHeadCell,
  TableCell,
  TableEmptyRow,
  TableGroupRow,
  TableHead,
  TableHeadCell,
  TableItem,
  TableRow,
} from "@/components/data-table";
import { Highlight } from "@/components/highlight";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { RoadmapItem, RoadmapPerson, RoadmapTriageEntry } from "@/lib/api";
import type { ApiErrorFallback } from "@/lib/api-errors";
import { effortLabel } from "@/lib/effort";
import { formatDay, formatList, formatMonth, formatNumber } from "@/lib/format";
import { firstNameLabel, type NamedPerson, normalEmail } from "@/lib/people";
import { cn } from "@/lib/utils";

import { BLOCKAGE_WRAP, BlockageSymbol, BlockageText } from "./blockers";
import { FollowButton } from "./follow-button";
import { StatusSymbol } from "./status-menu";
import type { TriageAction } from "./triage";
import { TriageGroup } from "./triage-rows";
import {
  ariaSort,
  type BlockageLine,
  blockageLines,
  followerStack,
  groupTotalLabel,
  hasToggle,
  itemEffort,
  type MovedItem,
  PRIORITY_TONE,
  priorityLabel,
  type RoadmapColumn,
  roadmapColumns,
  type RoadmapSort,
  sortHint,
  type StatusGroup,
  type TableGroupKey,
} from "./utils";

/** A change from a row that failed (a follow, a status move), for the page to say above the table. */
export interface RowFailure {
  error: unknown;
  /** What was tried, for the message when the server gave no code. */
  action: Extract<ApiErrorFallback, "followRoadmapItem" | "setRoadmapStatus">;
}

/** What every row reads besides its item: the viewer, the people and the other items. */
export interface RowContext {
  /** The viewer's email. */
  viewer: string;
  /** A developer moves an item's status from its row. */
  isDeveloper: boolean;
  /**
   * The viewer sees the hours (an owner and a developer): the effort's column
   * and each group's hours. An admin gets neither.
   */
  showHours: boolean;
  /** The month a developer plans an idea in from its row ("2026-11"). */
  planMonth: string;
  /** The dashboard's accounts with their roles, by email in lower case. */
  people: ReadonlyMap<string, RoadmapPerson>;
  /** Every item of the roadmap, by id, for the items a blockage names. */
  items: ReadonlyMap<string, { title: string; status: string }>;
  /** The search as typed, marked in titles and summaries. */
  search: string;
  /** Opens an item's details. */
  onOpen: (id: string) => void;
  /** A follow or a status move from a row failed. */
  onError: (failure: RowFailure) => void;
  /** A follow went through, or a status move starts: an earlier failure no longer holds. */
  onClearError: () => void;
  /** A status move from a row went through: the page sends the focus after the item. */
  onMoved: (moved: MovedItem) => void;
}

interface RoadmapTableProps extends RowContext {
  /** The month's groups in the page's order, each with its items as filtered and sorted. */
  groups: StatusGroup<RoadmapItem>[];
  sort: RoadmapSort | null;
  onSort: (column: RoadmapColumn) => void;
  /**
   * The triage entries the table shows, newest first: a group of their own
   * just above the ideas, none when empty (always for anyone but a developer).
   */
  triage: readonly RoadmapTriageEntry[];
  /** The people the triage names its submitters among (`triagePeople`), by email in lower case. */
  submitters: ReadonlyMap<string, NamedPerson>;
  /**
   * An action on an entry: Naar backlog opens the idea's dialog for it;
   * Samenvoegen met…, Sluiten and Verwijderen each ask first.
   */
  onEntryAction: (action: TriageAction, entry: RoadmapTriageEntry) => void;
  /** The groups that are folded: they keep their row, without their items. */
  folded: ReadonlySet<TableGroupKey>;
  /** Folds a group, or unfolds it. */
  onToggleGroup: (group: TableGroupKey) => void;
  /** Said in the one row of a table with nothing to show. */
  emptyText: string;
  /** Clears the filters; offered when they are what leaves the table empty. */
  onReset?: () => void;
  /** While another month's overview loads: the rows dim. */
  busy?: boolean;
}

/**
 * The roadmap in one dense table, in the Tools page's style: a group per
 * status the month on show has (Klaar, Bezig, Gepland and Ideeën in the
 * current month), each with its title and on the right its count and hours,
 * then its items. Every group folds from its row and keeps its count and
 * hours folded. A row shows the item's status (for a developer, a menu to
 * move it), its title (a button to its details), its summary and its open
 * blockages (`<BlockageLines>`), its priority, its effort, its month, its
 * deadline, its followers' avatars and, in a narrow last column, the
 * viewer's follow button (none on a done item), every column but the item's
 * centred. The follow button has a column of its own, so the avatars sit in
 * the middle of theirs and the buttons line up in every row. Every column
 * header sorts within the groups, and says how in its tooltip. Below 900 px
 * of the table's box the month and the deadline move under the summary
 * (`MID`), and in the phone layout the followers too, so the item, its
 * priority and its effort keep the row. An admin, who sees no hours, gets no
 * effort column and each group's count alone (`showHours`), and an idea
 * without an effort says "Nog te schatten" under its summary instead, so they
 * see which ideas cannot be planned yet. A developer's
 * triage, the ideas others proposed and the questions DC Data could not
 * answer, comes as a group of its own in solid De Ceuster blue just above the
 * ideas (`TriageGroup`).
 */
export function RoadmapTable({
  groups,
  triage,
  submitters,
  onEntryAction,
  sort,
  onSort,
  folded,
  onToggleGroup,
  emptyText,
  onReset,
  busy = false,
  ...context
}: RoadmapTableProps) {
  const { t } = useTranslation();
  const columns = roadmapColumns(context.showHours);
  // The sorting columns, then the follow button's narrow column.
  const colSpan = columns.length + 1;
  const shown = groups.filter((group) => group.items.length > 0);
  const statusGroup = (group: StatusGroup<RoadmapItem>) => {
    const expanded = !folded.has(group.status);
    return (
      <tbody key={group.status} data-group={group.status}>
        <TableGroupRow
          title={t(`roadmap.groups.${group.status}.title`)}
          colSpan={colSpan}
          collapse={{ expanded, onToggle: () => onToggleGroup(group.status) }}
          aside={groupTotalLabel(group.items, context.showHours)}
        />
        {expanded && group.items.map((item) => <ItemRow key={item.id} item={item} {...context} />)}
      </tbody>
    );
  };
  return (
    <DataTable label={t("nav.roadmap")} busy={busy}>
      <TableHead>
        {columns.map((column) => {
          const layout = columnLayout(column);
          return (
            <SortableHeadCell
              key={column}
              sort={ariaSort(sort, column)}
              onSort={() => onSort(column)}
              align={layout.align}
              narrow={layout.narrow}
              // Three columns share a phone's row only with the effort's: the arrow may go under
              // the name, so they fit; two fit without.
              wrap={context.showHours}
              // Ascending, then descending, then back to the default rank, as its tooltip says.
              hint={sortHint(column)}
              contentClassName={layout.mid ? MID.content : undefined}
              className={layout.cell}
            >
              {t(`roadmap.column.${column}`)}
            </SortableHeadCell>
          );
        })}
        <TableHeadCell align="center" narrow="empty" className="w-px">
          <span className="sr-only">{t("roadmap.followers.follow")}</span>
        </TableHeadCell>
      </TableHead>
      {shown.filter((group) => group.status !== "idea").map(statusGroup)}
      {triage.length > 0 && (
        <TriageGroup
          entries={triage}
          colSpan={colSpan}
          expanded={!folded.has("triage")}
          onToggle={() => onToggleGroup("triage")}
          people={submitters}
          search={context.search}
          onAction={onEntryAction}
          onClearError={context.onClearError}
        />
      )}
      {shown.filter((group) => group.status === "idea").map(statusGroup)}
      {shown.length === 0 && triage.length === 0 && (
        <tbody>
          <TableEmptyRow colSpan={colSpan} onReset={onReset}>
            {emptyText}
          </TableEmptyRow>
        </tbody>
      )}
    </DataTable>
  );
}

/**
 * The table's middle layout: below 900 px of its own box (a 1024 px window
 * with the sidebar open, a tablet) the month and the deadline leave their
 * columns for a line under the summary, so the item keeps its room. Their
 * cells stay, empty and without padding, like the followers' in the phone
 * layout (`narrow="empty"`). Full literal classes, so Tailwind generates them.
 */
const MID = {
  /** A month or deadline cell: no padding while its column is out. */
  cell: "@max-[900px]:p-0",
  /** Its content, gone while its column is out. */
  content: "contents @max-[900px]:hidden",
  /** The line under the summary that says it instead. */
  only: "@min-[900px]:hidden",
} as const;

/**
 * The priority's and the effort's cells keep less padding in the phone
 * layout, where they share the row with the item alone.
 */
const PHONE_TIGHT = "@max-[640px]:px-2";

/** How a column's head and cells sit in each layout. Every column but the item's is centred. */
function columnLayout(column: RoadmapColumn): {
  narrow: NarrowColumn;
  align: "left" | "center";
  cell?: string;
  mid: boolean;
} {
  switch (column) {
    case "month":
    case "deadline":
      return { narrow: "keep", align: "center", cell: MID.cell, mid: true };
    case "followers":
      return { narrow: "empty", align: "center", mid: false };
    case "priority":
    case "effort":
      return { narrow: "keep", align: "center", cell: PHONE_TIGHT, mid: false };
    case "item":
      return { narrow: "keep", align: "left", mid: false };
  }
}

interface ItemRowProps extends RowContext {
  item: RoadmapItem;
}

/** One item's row. */
function ItemRow({
  item,
  viewer,
  isDeveloper,
  showHours,
  planMonth,
  people,
  items,
  search,
  onOpen,
  onError,
  onClearError,
  onMoved,
}: ItemRowProps) {
  const { t } = useTranslation();
  const blockages = blockageLines(item, { people, items, viewer });
  const following = hasToggle(item, "followedByMe", viewer);
  const bell = item.can.follow ? (
    <FollowButton
      itemId={item.id}
      title={item.title}
      following={following}
      onError={(error) => onError({ error, action: "followRoadmapItem" })}
      onDone={onClearError}
    />
  ) : null;
  const avatars = <FollowerAvatars item={item} people={people} />;
  // What the month and deadline columns say, on one line under the summary while they are out.
  const dates = [
    item.month && formatMonth(item.month, "short"),
    item.deadline && t("roadmap.facts.deadline", { date: formatDay(item.deadline) }),
  ].filter((fact): fact is string => !!fact);

  return (
    <TableRow>
      <TableCell>
        <TableItem
          icon={
            <StatusSymbol
              item={item}
              isDeveloper={isDeveloper}
              planMonth={planMonth}
              onError={(error) => onError({ error, action: "setRoadmapStatus" })}
              onStart={onClearError}
              onMoved={onMoved}
            />
          }
          title={
            <>
              <button
                type="button"
                onClick={() => onOpen(item.id)}
                className="hover:text-primary focus-visible:ring-ring rounded-sm text-left font-medium hover:underline focus-visible:ring-2 focus-visible:outline-none"
              >
                <Highlight text={item.title} query={search} />
              </button>
              {item.comment_count > 0 && <CommentCount count={item.comment_count} />}
            </>
          }
          summary={
            item.summary && (
              <span className="line-clamp-2">
                <Highlight text={item.summary} query={search} />
              </span>
            )
          }
        >
          {/* Without the effort's column, an idea not estimated yet says so here: it cannot be planned. */}
          {!showHours && item.status === "idea" && !item.estimated && (
            <p className="text-muted mt-1 text-xs" data-unestimated="">
              {t("roadmap.effort.unknown")}
            </p>
          )}
          {dates.length > 0 && (
            <p className={cn("text-muted mt-1 text-xs wrap-anywhere", MID.only)}>
              {dates.join(", ")}
            </p>
          )}
          {blockages.lines.length > 0 && (
            <BlockageLines
              lines={blockages.lines}
              more={blockages.more}
              language={item.language}
              onOpen={onOpen}
            />
          )}
          {/* The followers' column is out in the phone layout: they come last under the item. */}
          {(bell || item.followers.length > 0) && (
            <div
              className={cn("mt-1.5 inline-flex flex-wrap items-center gap-1.5", NARROW_ONLY)}
              data-followers=""
            >
              {bell}
              {avatars}
            </div>
          )}
        </TableItem>
      </TableCell>
      <TableCell align="center" className={PHONE_TIGHT}>
        <Chip tone={PRIORITY_TONE[item.priority] ?? "neutral"}>{priorityLabel(item.priority)}</Chip>
      </TableCell>
      {showHours && (
        <TableCell align="center" className={cn("whitespace-nowrap", NARROW_WRAP, PHONE_TIGHT)}>
          <EffortText item={item} />
        </TableCell>
      )}
      <TableCell align="center" className={cn("text-muted whitespace-nowrap", MID.cell)}>
        <span className={MID.content}>{item.month ? formatMonth(item.month, "short") : "—"}</span>
      </TableCell>
      <TableCell
        align="center"
        className={cn("text-muted whitespace-nowrap tabular-nums", MID.cell)}
      >
        <span className={MID.content}>{item.deadline ? formatDay(item.deadline) : "—"}</span>
      </TableCell>
      <TableCell align="center" narrow="empty" className="py-1">
        <div className="inline-flex align-middle" data-followers="">
          {avatars}
        </div>
      </TableCell>
      <TableCell align="center" narrow="empty" className="w-px py-1 pl-0">
        {bell}
      </TableCell>
    </TableRow>
  );
}

/** How many comments an item has, after its title: a speech bubble and the number. */
function CommentCount({ count }: { count: number }) {
  const { t } = useTranslation();
  return (
    <span
      className="text-muted ml-1.5 inline-flex items-center gap-0.5 align-middle text-xs font-normal whitespace-nowrap tabular-nums"
      data-comment-count=""
    >
      <MessageSquare aria-hidden="true" className="size-3.5" />
      <span aria-hidden="true">{formatNumber(count)}</span>
      <span className="sr-only">{t("roadmap.comments.count", { count })}</span>
    </span>
  );
}

interface BlockageLinesProps {
  lines: BlockageLine[];
  /** How many open blockages there are besides these lines. */
  more: number;
  /** The language the asks were written in: the item's. */
  language: string;
  onOpen: (id: string) => void;
}

/**
 * An item's open blockages under its summary, one line each: the blockage
 * symbol, then who and what ("An: de prijslijst bezorgen"), an item as a
 * button to its details with its status in brackets. A line that names the
 * viewer is emphasised. At most three, then "+2 andere blokkades".
 */
function BlockageLines({ lines, more, language, onOpen }: BlockageLinesProps) {
  const { t } = useTranslation();
  return (
    <ul className="mt-1 space-y-0.5 text-xs" data-blockers="">
      {lines.map((line) => (
        <li
          key={line.id}
          className={cn(
            "flex items-start gap-1.5",
            BLOCKAGE_WRAP,
            line.mine ? "text-primary font-medium" : "text-muted",
          )}
          data-mine={line.mine || undefined}
        >
          <BlockageSymbol className="mt-px size-3.5" />
          <span className="min-w-0">
            <BlockageText line={line} language={language} onOpenItem={onOpen} />
          </span>
        </li>
      ))}
      {more > 0 && (
        <li className="text-muted pl-5">{t("roadmap.blockers.more", { count: more })}</li>
      )}
    </ul>
  );
}

/**
 * An item's effort in the table: its shown estimate, "Nog te schatten"
 * without one, and on a done item the hours it used with "werkelijk" (its
 * actual hours) or "geschat" (its estimate counted) under it.
 */
export function EffortText({ item }: { item: RoadmapItem }) {
  const { t } = useTranslation();
  const effort = itemEffort(item);
  if (effort.hours === null) {
    return <span className="text-muted">{t("roadmap.effort.unknown")}</span>;
  }
  return (
    <>
      <span className="text-foreground">{effortLabel(effort.hours)}</span>
      {item.status === "done" && (
        <span className="text-muted block text-xs">
          {effort.actual ? t("roadmap.effort.actual") : t("roadmap.effort.estimated")}
        </span>
      )}
    </>
  );
}

interface FollowerAvatarsProps {
  item: RoadmapItem;
  people: ReadonlyMap<string, RoadmapPerson>;
}

/**
 * An item's followers: the latest three followers' avatars stacked, each
 * overlapping the one before with a ring in the page's colour between them
 * and the newest on top, then "+2" for those who followed earlier. Hovering
 * or focusing the stack names every follower by first name with their role
 * ("Davy, eigenaar"). Nothing without followers.
 */
function FollowerAvatars({ item, people }: FollowerAvatarsProps) {
  const { t } = useTranslation();
  const stack = followerStack(item.followers, people);
  if (stack.avatars.length === 0) {
    return null;
  }
  const name = firstNameLabel(people.values());
  const plainNames = item.followers.map((email) =>
    name(people.get(normalEmail(email)) ?? { email }),
  );
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className="focus-visible:ring-ring flex items-center rounded-full focus-visible:ring-2 focus-visible:outline-none"
        >
          <span className="sr-only">
            {t("roadmap.followers.followedBy", { names: formatList(plainNames) })}
          </span>
          <span aria-hidden="true" className="flex">
            {stack.avatars.map((avatar, index) => (
              <span
                key={avatar.email}
                className={cn(
                  "bg-primary text-primary-foreground ring-background inline-flex size-6 items-center justify-center rounded-full text-[0.625rem] font-semibold ring-2",
                  // Overlapping, yet leaving each one's letters readable.
                  index > 0 && "-ml-1",
                )}
              >
                {avatar.initials}
              </span>
            ))}
          </span>
          {stack.more > 0 && (
            <span aria-hidden="true" className="text-muted ml-1 text-xs tabular-nums">
              {t("roadmap.followers.more", { count: stack.more })}
            </span>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent className="text-left">
        <ul>
          {stack.names.map((name, index) => (
            <li key={item.followers[index]}>{name}</li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  );
}
